import React, { FC, useState, useEffect } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import * as Sentry from '@sentry/react'
import { useApolloClient } from '@apollo/client'
import { EmptyState } from '../EmptyState'
import { SkeletonLoader } from '../SkeletonLoader'
import { IssueListItem } from '.'
import useIssueList from '../../redux/issue-list/useIssueList'
import { SearchIssues } from '../../graphql/Issue/__generated__/SearchIssues'
import { searchIssuesQuery } from '../../graphql/Issue/searchIssues'
import { useNotifications } from '../Notifications'

const IssueList: FC = () => {
  const {
    issues,
    page,
    loading,
    categories,
    search,
    platform,
    actions: { clearAndSetCategory, addIssues, changePage }
  } = useIssueList()
  const apollo = useApolloClient()
  const { addNotification } = useNotifications()
  const [hasNext, setHasNext] = useState(true)

  useEffect(() => {
    const handleIssues = async () => {
      try {
        const { data } = await apollo.query<SearchIssues>({
          fetchPolicy: 'no-cache',
          query: searchIssuesQuery,
          variables: {
            page,
            search,
            platform,
            categories
          }
        })

        addIssues(data.searchIssue?.docs || [])
        setHasNext(!!data.searchIssue?.hasNextPage)
      }
      catch (err) {
        addNotification({ message: err.message, type: 'error', interval: 10000 })
        Sentry.captureException(err)
      }
    }

    if (loading) {
      handleIssues()
    }
  }, [loading])

  return (
    <>
      {!issues.length && !loading && (
        <EmptyState
          title="Whoops, nothing came up."
          suggestion={[
            <span>
              Try exploring <a onClick={() => clearAndSetCategory('sales')}>Sales</a> to learn how
              to turn your audience into customers.
            </span>,
            <span>
              Try exploring <a onClick={() => clearAndSetCategory('planning')}>Planning</a> to set
              yourself up for success.
            </span>,
            <span>
              Try exploring <a onClick={() => clearAndSetCategory('engagement')}>Engagement</a> to
              find out how to build your audience.
            </span>,
            <span>
              Try exploring <a onClick={() => clearAndSetCategory('captions')}>Captions</a> to learn
              about finding your own voice.
            </span>
          ]}
        />
      )}

      {issues.length === 0 && loading && <SkeletonLoader quantity={10} type="issue-list-item" />}

      {issues.length > 0 && (
        <InfiniteScroll
          dataLength={issues.length}
          next={() => changePage(page + 1)}
          hasMore={hasNext}
          loader={null}
        >
          <div className="issues-list">
            {issues.map((issue) => (
              <IssueListItem issue={issue} key={issue._id} />
            ))}
          </div>
        </InfiniteScroll>
      )}
    </>
  )
}

export default IssueList
